import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, Label, TextInput } from "flowbite-react";
import CustomerInvoiceApi from "../../Api/CustomerInvoiceApi";
import { CustomerInvoiceSummary } from "../../Models/CustomerInvoice";
import Loader from "../Common/Loader";
import { format } from "date-fns";

const money = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(n);

/** API may send decimals as numbers or strings depending on serializer config. */
const asDecimal = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function CustomerInvoiceList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CustomerInvoiceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoiceLookup, setInvoiceLookup] = useState("");

  useEffect(() => {
    setLoading(true);
    setError(null);
    CustomerInvoiceApi.getMyInvoices(0, 100)
      .then((res) => {
        setRows(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        const msg =
          err.response?.data?.message ??
          (err.response?.status === 403
            ? "Invoice lookup is not enabled for this account."
            : "Could not load invoices.");
        setError(typeof msg === "string" ? msg : "Could not load invoices.");
      });
  }, []);

  const goLookup = () => {
    const n = parseInt(invoiceLookup.trim(), 10);
    if (!Number.isFinite(n) || n <= 0) return;
    navigate(`/my-invoices/${n}`);
  };

  if (loading) return <Loader />;

  return (
    <div className="w-full max-w-5xl mx-auto p-4">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">My invoices</h1>
      <p className="text-gray-600 mb-6">
        Signed-in dealers can view invoices tied to their Price Bros billing
        profile (RIMSS customer). Use{" "}
        <span className="font-medium">Print</span> on an invoice to save a
        PDF.
      </p>

      {error && (
        <Alert color="failure" className="mb-6">
          {error}
        </Alert>
      )}

      {!error && (
        <div className="flex flex-col sm:flex-row gap-4 mb-8 items-end">
          <div className="flex-1 w-full">
            <Label htmlFor="inv">Invoice number</Label>
            <TextInput
              id="inv"
              value={invoiceLookup}
              onChange={(e) => setInvoiceLookup(e.target.value)}
              placeholder="e.g. 2031311"
              className="mt-1"
            />
          </div>
          <Button type="button" className="buttonMain" onClick={goLookup}>
            Open invoice
          </Button>
        </div>
      )}

      {!error && rows.length === 0 && (
        <p className="text-gray-700">No invoices were found yet.</p>
      )}

      {!error && rows.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="bg-gray-100 text-gray-900 uppercase text-xs">
              <tr>
                <th className="px-4 py-3">Invoice #</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">PO</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.invoiceNumber} className="border-t border-gray-200">
                  <td className="px-4 py-3 font-medium">{r.invoiceNumber}</td>
                  <td className="px-4 py-3">
                    {r.invoiceDate
                      ? format(new Date(r.invoiceDate), "MMM d, yyyy")
                      : "—"}
                  </td>
                  <td className="px-4 py-3">{r.customerPo ?? "—"}</td>
                  <td className="px-4 py-3 text-right font-medium">
                    {money(asDecimal(r.documentTotal))}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/my-invoices/${r.invoiceNumber}`}
                      className="text-blue-600 hover:underline"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
